import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Loader2,
  Users,
  UserPlus,
  Trash2,
  ShieldAlert,
  Mail,
  Crown,
  Clock,
} from "lucide-react";

function planLabel(plan) {
  if (!plan) return "Free";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

function SeatBar({ used, limit }) {
  const pct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const tone =
    pct >= 100 ? "bg-red-600" : pct >= 75 ? "bg-amber-500" : "bg-emerald-600";
  return (
    <div
      data-testid="team-seat-bar"
      className="h-2 w-full overflow-hidden bg-slate-100"
    >
      <div
        className={`h-full ${tone} transition-all`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [cancelInviteTarget, setCancelInviteTarget] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/team");
      setData(data);
    } catch (err) {
      toast.error("Could not load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (loading) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </main>
    );
  }
  if (!data) return null;

  const { owner, is_owner, members, pending_invites, seats } = data;
  const seatFull = seats.available <= 0;

  return (
    <main
      data-testid="team-page"
      className="mx-auto max-w-5xl space-y-8 px-6 py-10"
    >
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-blue-700">
            Team
          </div>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight">
            Your team
          </h1>
          <p className="mt-1 text-sm text-black">
            {is_owner
              ? "Invite teammates to share interviews, reviews, and pipelines."
              : `You're a member of ${owner.name || owner.email}'s team.`}
          </p>
        </div>
        {is_owner && (
          <Button
            data-testid="team-invite-btn"
            onClick={() => setInviteOpen(true)}
            disabled={seatFull}
            className="rounded-sm bg-blue-700 text-white hover:bg-blue-600 disabled:bg-slate-200 disabled:text-slate-500"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Invite teammate
          </Button>
        )}
      </header>

      {/* Seat usage card */}
      <section className="border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
              Seat usage
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span
                data-testid="team-seats-used"
                className="font-heading text-3xl font-semibold tabular-nums text-black"
              >
                {seats.used}
              </span>
              <span className="text-base text-black">of</span>
              <span
                data-testid="team-seats-limit"
                className="font-heading text-3xl font-semibold tabular-nums text-black"
              >
                {seats.limit}
              </span>
              <span className="text-base text-slate-500">
                {seats.limit === 1 ? "seat" : "seats"} used
              </span>
            </div>
            <div className="mt-1 text-sm text-black">
              Plan: <strong>{planLabel(seats.plan)}</strong>
              {seats.pending_count > 0 && (
                <span className="ml-3 text-slate-500">
                  · {seats.pending_count} pending invite{seats.pending_count === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
          <div className="w-full max-w-sm">
            <SeatBar used={seats.used} limit={seats.limit} />
            <div className="mt-2 flex justify-between text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              <span>Used</span>
              <span
                data-testid="team-seats-available"
                className={
                  seatFull ? "text-red-600" : "text-emerald-700"
                }
              >
                {seats.available} seat{seats.available === 1 ? "" : "s"} left
              </span>
            </div>
          </div>
        </div>
        {seatFull && is_owner && (
          <div
            data-testid="team-seats-full-banner"
            className="mt-5 flex items-start gap-3 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-none" />
            <div className="flex-1">
              You've used every seat on your plan. Upgrade or remove a teammate before inviting more.
            </div>
            <Button
              data-testid="team-manage-seats-btn"
              size="sm"
              onClick={async () => {
                const t = toast.loading("Opening Stripe Customer Portal…");
                try {
                  const { data } = await api.post("/billing/portal");
                  window.location.href = data.url;
                } catch (err) {
                  toast.error(
                    err?.response?.data?.detail ||
                      "Could not open billing portal",
                    { id: t }
                  );
                }
              }}
              className="rounded-sm bg-amber-600 text-white hover:bg-amber-700"
            >
              Manage seats
            </Button>
          </div>
        )}
      </section>

      {/* Members */}
      <section className="border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="flex items-center gap-2 font-heading text-base font-medium">
            <Users className="h-4 w-4 text-slate-600" />
            Members ({members.length})
          </h2>
        </div>
        <Table data-testid="team-members-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="w-24 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.id} data-testid={`team-member-${m.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2 font-medium text-black">
                    {m.is_owner && (
                      <Crown className="h-3.5 w-3.5 text-amber-500" />
                    )}
                    {m.name || m.email.split("@")[0]}
                  </div>
                  <div className="text-xs text-black">{m.email}</div>
                </TableCell>
                <TableCell className="text-sm text-black">
                  {m.is_owner ? "Owner" : "Member"}
                </TableCell>
                <TableCell className="text-sm text-black">
                  {m.joined_at
                    ? new Date(m.joined_at).toLocaleDateString()
                    : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {is_owner && !m.is_owner && (
                    <Button
                      data-testid={`team-remove-${m.id}`}
                      onClick={() => setRemoveTarget(m)}
                      variant="ghost"
                      size="sm"
                      className="rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Remove
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      {/* Pending invites */}
      {pending_invites.length > 0 && (
        <section className="border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="flex items-center gap-2 font-heading text-base font-medium">
              <Mail className="h-4 w-4 text-slate-600" />
              Pending invites ({pending_invites.length})
            </h2>
          </div>
          <Table data-testid="team-invites-table">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-24 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending_invites.map((inv) => (
                <TableRow key={inv.id} data-testid={`team-invite-${inv.id}`}>
                  <TableCell className="text-sm text-black">{inv.email}</TableCell>
                  <TableCell className="text-sm text-black">
                    {inv.created_at
                      ? new Date(inv.created_at).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-black">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {inv.expires_at
                        ? new Date(inv.expires_at).toLocaleDateString()
                        : "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {is_owner && (
                      <Button
                        data-testid={`team-cancel-invite-${inv.id}`}
                        onClick={() => setCancelInviteTarget(inv)}
                        variant="ghost"
                        size="sm"
                        className="rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={refresh}
        disabled={seatFull}
      />

      <AlertDialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove teammate?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget &&
                `${removeTarget.email} will lose access to your team. They'll be signed out of all devices immediately and can be re-invited later.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="team-remove-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="team-remove-confirm"
              onClick={async () => {
                if (!removeTarget) return;
                try {
                  await api.delete(`/team/members/${removeTarget.id}`);
                  toast.success("Teammate removed");
                  setRemoveTarget(null);
                  refresh();
                } catch (err) {
                  toast.error(
                    err?.response?.data?.detail || "Failed to remove"
                  );
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!cancelInviteTarget}
        onOpenChange={(o) => !o && setCancelInviteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this invite?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelInviteTarget &&
                `The invite link sent to ${cancelInviteTarget.email} will stop working.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep invite</AlertDialogCancel>
            <AlertDialogAction
              data-testid="team-cancel-invite-confirm"
              onClick={async () => {
                if (!cancelInviteTarget) return;
                try {
                  await api.delete(`/team/invites/${cancelInviteTarget.id}`);
                  toast.success("Invite cancelled");
                  setCancelInviteTarget(null);
                  refresh();
                } catch (err) {
                  toast.error(
                    err?.response?.data?.detail || "Failed to cancel"
                  );
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Cancel invite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

function InviteDialog({ open, onClose, onInvited, disabled }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setEmail("");
    setName("");
    setSubmitting(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setSubmitting(true);
    try {
      await api.post("/team/invites", { email, name });
      toast.success(`Invite sent to ${email}`);
      reset();
      onClose();
      onInvited();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent data-testid="team-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite a teammate</DialogTitle>
          <DialogDescription>
            They'll get an email with a one-click link to join your team. The
            link expires in 7 days.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div>
            <Label htmlFor="invite-email" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
              Work email
            </Label>
            <Input
              id="invite-email"
              data-testid="team-invite-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 rounded-sm border-slate-300"
              placeholder="teammate@company.com"
            />
          </div>
          <div>
            <Label htmlFor="invite-name" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
              Their name (optional)
            </Label>
            <Input
              id="invite-name"
              data-testid="team-invite-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 rounded-sm border-slate-300"
              placeholder="Alex Carter"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onClose();
              }}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="team-invite-submit-btn"
              disabled={submitting || disabled}
              className="rounded-sm bg-blue-700 hover:bg-blue-600"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
